import { Component, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { LiferayService } from '../../../services/liferay/liferay.service';
import { LIFERAY_WEB_CONSTANT } from '../../../utils/liferay.constant';
import { IContractData } from '../../../interfaces/IJob';
import { DataService } from '../../../services/data/data.service';
import { UtilitiesService } from '../../../services/utilities/utilities.service';
import { Subject, takeUntil } from 'rxjs';
import { ICrossSellContent, ICrossSellContentLiferayResponse } from '../../../interfaces/ICrossSellContent';
import { Ga4Service } from '../../../services/ga4/ga4.service';
import { ACC_LOGGEDIN, CROSS_SELL_CE_DASHBOARD_LOCATION, CROSS_SELL_CE_LANDING_PAGE_BUTTON, CROSS_SELL_CE_SR_CONFIRMATION, CROSS_SELL_SURGE_DASHBOARD_LOCATION, CROSS_SELL_SURGE_SR_CONFIRMATION, CROSS_SELL_SURGE_LANDING_PAGE_BUTTON, CROSS_SELL_CE_MY_PLAN_LOCATION } from '../../../services/ga4/ga4.constant';
import { ACH_AUTO, CLIENT_ID, CROSS_SELL_CE_CARD_DARK_VARIANT_ID, CROSS_SELL_CE_CARD_THEME_EXPERIMENT_ID, CROSS_SELL_CE_ENROLLMENT_VARIANT_ID, CROSS_SELL_CE_EXPERIMENT_ID, CROSS_SELL_PRICE, PAYMENTECH, SELECTED_CONTRACT_DATA } from '../../../utils/constants';
import { RouterService } from '../../../services/router/router.service';
import { APP_ROUTES } from '../../../utils/route.constant';
declare const window: any;
import { FEATURES_CONSTANT } from '../../../utils/features.constant';
import { IFeatureFlag } from '../../../interfaces/IFeatureFlags';
import { EnrollmentService } from '../../../services/enrollment/enrollment.service';
import { getMonthlyCost } from '../../pages/special-member-offer-v2/special-member-offer-v2.helper';
import { trackByIndex } from '../../../utils/helper';
import { MY_PLAN_CE_CROSS_SELL_CONTENT } from './cross-sell.constants';

export type CrossSellTheme = 'light' | 'dark';

@Component({
  selector: 'app-cross-sell',
  templateUrl: './cross-sell.component.html',
  styleUrl: './cross-sell.component.scss'
})
export class CrossSellComponent implements OnInit, OnDestroy, OnChanges {
  contractData: IContractData | null = null;
  contentToBeDisplayed: ICrossSellContent | null = null;
  cleaner$ = new Subject<void>();
  @Input() callButtonLocation: 'dashboard' | 'srConfirmation' | 'myPlan' = 'dashboard';
  @Input() type = 'CE';
  @Input() reloadKey: number = 0;
  @Input() funnelName: string = ACC_LOGGEDIN;
  isCrossSellV2Enabled: boolean = false;
  myPlanCardContent = MY_PLAN_CE_CROSS_SELL_CONTENT;
  monthlyCost: number | null = null;
  trackByIndex = trackByIndex;
  isCrossSellCardV2Enabled: boolean = false;
  currentTheme: CrossSellTheme = 'light';
  currentThemeID: string = '';
  optimizelyListenerTokens: string[] = [];
  crossSellPrice = CROSS_SELL_PRICE;

  get showNewCardUi(): boolean {
    return this.type === 'CE' && this.isCrossSellCardV2Enabled;
  }

  constructor(
    private liferay: LiferayService,
    private data: DataService,
    private utils: UtilitiesService,
    private ga4Service: Ga4Service,
    private router: RouterService,
    private enrollService: EnrollmentService,
    private ngZone: NgZone
  ) { }

  get isMyPlanCard(): boolean {
    return this.callButtonLocation === 'myPlan';
  }

  ngOnInit(): void {
    this.evaluateTheme();
    this.setupOptimizelyReadiness();

    this.data.contractData$
      .pipe(takeUntil(this.cleaner$))
      .subscribe((data) => {
        this.contractData = data;
        this.contractData && this.getCrossSellContent();
      });

    this.data.featureFlags$
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (featureFlags) => {
          this.isCrossSellV2Enabled = featureFlags?.features
            ?.find((feature: IFeatureFlag) => feature.feature === FEATURES_CONSTANT.CROSS_SELL_V2)?.flag || false;
          this.isCrossSellCardV2Enabled = featureFlags?.features
            ?.find((feature: IFeatureFlag) => feature.feature === FEATURES_CONSTANT.CROSS_SELL_CARD_V2)?.flag || false;
        },
        error: (error) => {
          console.error('Error fetching feature flags', error);
          this.isCrossSellV2Enabled = false;
          this.isCrossSellCardV2Enabled = false;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['reloadKey']) {
      this.contractData && this.getCrossSellContent();
      this.evaluateTheme();
    }
  }

  evaluateTheme(): void {
    try {
      const experimentStates = window.optimizely?.get?.('state')?.getExperimentStates?.();
      const crossSellExperiment = experimentStates?.[CROSS_SELL_CE_CARD_THEME_EXPERIMENT_ID];
      const variationName = (crossSellExperiment?.variation?.name || '').toLowerCase();
      const variationId = crossSellExperiment?.variation?.id;
      this.currentThemeID = variationId || '';

      if (variationName.includes('dark') || variationId === CROSS_SELL_CE_CARD_DARK_VARIANT_ID) {
        this.currentTheme = 'dark';
      } else {
        this.currentTheme = 'light';
      }
    } catch {
      this.currentTheme = 'light';
      this.currentThemeID = '';
    }
  }

  setupOptimizelyReadiness(): void {
    try {
      window.optimizely = window.optimizely || [];
      if (typeof window.optimizely.push === 'function') {
        const token = `cross_sell_theme_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        this.optimizelyListenerTokens.push(token);

        window.optimizely.push({
          type: 'addListener',
          filter: {
            type: 'lifecycle',
            name: 'campaignDecided'
          },
          token,
          handler: () => {
            this.ngZone.run(() => {
              if (!this.cleaner$.isStopped) {
                this.evaluateTheme();
              }
            });
          }
        });
      }
    } catch {
      // Ignored
    }
  }

  ngOnDestroy(): void {
    this.cleaner$.next();
    this.cleaner$.complete();
    this.teardownOptimizelyListeners();
  }

  private teardownOptimizelyListeners(): void {
    try {
      while (this.optimizelyListenerTokens.length > 0) {
        const token = this.optimizelyListenerTokens.pop()!;

        if (Array.isArray(window.optimizely)) {
          for (let i = window.optimizely.length - 1; i >= 0; i--) {
            const item = window.optimizely[i];
            if (item?.type === 'addListener' && item?.token === token) {
              window.optimizely.splice(i, 1);
            }
          }
        }

        if (typeof window.optimizely?.push === 'function') {
          window.optimizely.push({
            type: 'removeListener',
            token
          });
        }
      }
    } catch {
      // Ignored
    }
  }

  getCrossSellContent(): void {
    const { type, id } = this.type === 'CE' ? LIFERAY_WEB_CONSTANT.cross_sell_ce_content : LIFERAY_WEB_CONSTANT.cross_sell_surge_content;
    this.liferay.getWebContent<ICrossSellContentLiferayResponse>(type, id)
      .pipe(takeUntil(this.cleaner$))
      .subscribe((webContent) => {
        if (!webContent) return;

        const contentToDisplayForClient = webContent.value?.content || [];
        const clientId = this.contractData?.baseContract?.clientId;
        const contentFound = contentToDisplayForClient.find(item => item.clientId === clientId) || null;
        this.contentToBeDisplayed = contentFound
          ? { ...contentFound, hoursOfOperation: this.utils.setHtmlContent(contentFound.hoursOfOperation || '') }
          : null;
        this.isMyPlanCard && this.getMonthlyCost();
      });
  }

  /**
   * @description Fetches the CE product price for the contract's zip code to show the
   * "Starting at" monthly price on the My Plan card. The card is hidden if the price can't be fetched.
   */
  getMonthlyCost(): void {
    const productId = this.contentToBeDisplayed?.productId;
    const zipCode = this.contractData?.baseContract?.propertyAddress?.zip;
    this.monthlyCost = null;
    if (!productId || !zipCode) {
      return;
    }

    this.enrollService.getBaseCoverage({ productId, zipCode })
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (response) => {
          this.monthlyCost = getMonthlyCost(response?.productInfo?.cost) || null;
          this.monthlyCost && this.data.crossSellCEMyPlanCardDisplayed$.next();
        },
        error: () => {
          this.monthlyCost = null;
        }
      });
  }

  isProfileReadyForEnrollment(): boolean {
    return this.isProfileReadyForContact() && this.isProfileReadyForPayment();
  }

  isProfileReadyForContact(): boolean {
    return !!(this.contractData?.contact?.emailAddress
      && this.contractData?.contact?.phoneList?.some((phone) => phone.phoneNumber)
    );
  }

  isProfileReadyForPayment(): boolean {
    // This condition checks if the payment method is Credit Card or Bank Account
    // Others billMechCodes are not supported for this flow
    return (this.contractData?.paymentInfo?.billMechCode === PAYMENTECH && !!this.contractData?.paymentInfo?.creditCardNumber) ||
      (this.contractData?.paymentInfo?.billMechCode === ACH_AUTO && !!this.contractData?.paymentInfo?.bankAcctNumber);
  }

  getRouteToNavigate(): string {
    const activeVariant = this.ABTest();
    // if experiment is not running means normal eligibility condition and navigate them between form or enrollment
    if (!activeVariant) {
      return this.isProfileReadyForEnrollment() ? APP_ROUTES.SPECIAL_MEMBER_OFFER_V2 : APP_ROUTES.SPECIAL_MEMBER_OFFER_FORM_V2;
    }

    // with AB testing, check variant and even if its enrollment variant, check for eliginility and then navigate to enrollment
    if (activeVariant === CROSS_SELL_CE_ENROLLMENT_VARIANT_ID) {
      return this.isProfileReadyForEnrollment() ? APP_ROUTES.SPECIAL_MEMBER_OFFER_V2 : APP_ROUTES.SPECIAL_MEMBER_OFFER_FORM_V2;
    }
    // navigate to form variant
    return APP_ROUTES.SPECIAL_MEMBER_OFFER_FORM_V2;
  }

  learnAboutOffer(): void {
    sessionStorage.setItem(SELECTED_CONTRACT_DATA, JSON.stringify(this.contractData));
    sessionStorage.setItem('source', this.funnelName.toLowerCase().includes('vivr') ? 'vivr' : this.callButtonLocation);
    this.handleGa4Event();

    // feature flag not enabled means previous release version form to be displayed
    // feature flag enabled means new changes to be displayed with AB testing on or off result
    const route = this.isCrossSellV2Enabled ? this.getRouteToNavigate() : APP_ROUTES.SPECIAL_MEMBER_OFFER_FORM;
    const queryParams: any = {};
    queryParams['type'] = this.type;
    if (this.data.isVivr$.getValue()) {
      const token = this.data.vivrAccessToken$.getValue();
      const source = this.data.vivrSub$.getValue();
      if (token) queryParams['access_token'] = token;
      if (source) queryParams['sourceId'] = source;
      this.router.openInNewTab(route, { queryParams });
      return;
    } else {
      this.router.openInNewTab(route, { queryParams });
    }
  }

  private trackCrossSellEvent(buttonConstant: string, typeKey: 'CE' | 'SURGE'): void {
    const locationMap: Record<'CE' | 'SURGE', Record<string, string>> = {
      CE: {
        dashboard: CROSS_SELL_CE_DASHBOARD_LOCATION,
        confirmation: CROSS_SELL_CE_SR_CONFIRMATION,
        myPlan: CROSS_SELL_CE_MY_PLAN_LOCATION,
      },
      SURGE: {
        dashboard: CROSS_SELL_SURGE_DASHBOARD_LOCATION,
        confirmation: CROSS_SELL_SURGE_SR_CONFIRMATION,
      }
    };

    const locationKey = this.callButtonLocation === 'srConfirmation' ? 'confirmation' : this.callButtonLocation;
    const clientName = this.contractData?.baseContract?.clientId?.toUpperCase() === CLIENT_ID.SEARS ? 'Sears' : 'Cinch_DTC';

    const pageLocation = locationMap[typeKey][locationKey];
    this.ga4Service.buttonClickEvent(pageLocation, `${buttonConstant}_${clientName}`);
  }

  handleGa4Event(): void {
    const typeKey = this.type === 'CE' ? 'CE' : 'SURGE';
    const buttonConstant = typeKey === 'CE' ? CROSS_SELL_CE_LANDING_PAGE_BUTTON : CROSS_SELL_SURGE_LANDING_PAGE_BUTTON;
    this.trackCrossSellEvent(buttonConstant, typeKey);
  }

  ABTest(): string {
    try {
      const experimentStates = window.optimizely?.get?.('state')?.getExperimentStates?.();
      const crossSellExperiment = experimentStates?.[CROSS_SELL_CE_EXPERIMENT_ID];
      let activeVariantId = crossSellExperiment?.variation?.id;

      if (!crossSellExperiment) {
        return '';
      }

      // Track the click event in Optimizely for conversion measurement
      if (typeof window.optimizely?.push === 'function') {
        window.optimizely.push({
          type: 'event',
          eventName: 'click_learn_about_this_offer',
          tags: {
            experimentId: CROSS_SELL_CE_EXPERIMENT_ID,
            variantId: activeVariantId,
            timestamp: new Date().toISOString()
          }
        });
      }

      return activeVariantId || '';
    } catch (error) {
      console.warn('[Optimizely] Error evaluating ABTest:', error);
      return '';
    }
  }
}
